import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';

const PLACES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PLACE_LABELS = {
  1: '1st Place', 2: '2nd Place', 3: '3rd Place', 4: '4th Place',
  5: '5th Place', 6: '6th Place', 7: '7th Place', 8: '8th Place',
  9: '9th Place', 10: '10th Place',
};
const PLACE_MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

export default function AdminPayoutPage() {
  const api = useApi();
  const [enabled, setEnabled] = useState(false);
  const [payouts, setPayouts] = useState({ 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '', 8: '', 9: '', 10: '', last: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [paymentSummary, setPaymentSummary] = useState(null); // { entryFee, paidCount, unpaidCount }

  useEffect(() => {
    Promise.all([
      api.adminGetPayoutConfig(),
      api.adminGetPaymentConfig().catch(() => null),
      api.adminGetBrackets().catch(() => []),
    ]).then(([cfg, paymentCfg, brackets]) => {
        setEnabled(cfg.enabled || false);
        setPayouts({ 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '', 8: '', 9: '', 10: '', last: '', ...(cfg.payouts || {}) });
        if (paymentCfg && Array.isArray(brackets)) {
          const entryFee = Number(paymentCfg.entryFee) || 0;
          const paidCount = brackets.filter(b => b.paid).length;
          const unpaidCount = brackets.length - paidCount;
          setPaymentSummary({ entryFee, paidCount, unpaidCount, total: brackets.length });
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function updatePayout(place, val) {
    setPayouts(p => ({ ...p, [place]: val }));
  }

  async function handleSave() {
    setSaving(true); setError(''); setStatus('');
    try {
      // Convert to numbers, treat empty/blank as 0
      const cleaned = {};
      [...PLACES, 'last'].forEach(p => {
        cleaned[p] = Number(payouts[p]) || 0;
      });
      await api.adminSavePayoutConfig({ enabled, payouts: cleaned });
      setStatus('Payout settings saved.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const totalPot = [...PLACES, 'last'].reduce((sum, p) => sum + (Number(payouts[p]) || 0), 0);
  const activePlaces = [...PLACES, 'last'].filter(p => Number(payouts[p]) > 0).length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)' }}>Loading...</div>;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue)', letterSpacing: '2px', marginBottom: 20 }}>
        PAYOUT SETTINGS
      </h2>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>
        {error}
        <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>&#x2715;</button>
      </div>}
      {status && <div className="alert alert-success" style={{ marginBottom: 16 }}>{status}</div>}

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '1.1rem' }}>CONFIGURATION</h3>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontFamily: 'var(--font-condensed)', fontSize: '0.9rem',
            color: enabled ? 'var(--green)' : 'var(--gray)',
          }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            {enabled ? 'Payout Tab Enabled (visible to users)' : 'Payout Tab Disabled (hidden from users)'}
          </label>
        </div>

        <div className="alert alert-info" style={{ marginBottom: 20, fontSize: '0.88rem' }}>
          Enter payout amounts for each place. Leave blank or enter $0 to hide that place from users.
          Only places with a payout amount will be shown on the Payout page.
        </div>

        <table className="leaderboard-table" style={{ marginBottom: 20 }}>
          <thead>
            <tr>
              <th style={{ width: 160 }}>Place</th>
              <th style={{ width: 200 }}>Payout Amount ($)</th>
              <th style={{ color: 'var(--gray)', fontWeight: 400, fontSize: '0.82rem' }}>Leave blank or $0 to hide</th>
            </tr>
          </thead>
          <tbody>
            {PLACES.map(place => (
              <tr key={place}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.2rem' }}>{PLACE_MEDALS[place] || '🏅'}</span>
                    <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700 }}>{PLACE_LABELS[place]}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)' }}>$</span>
                    <input
                      className="form-input"
                      type="number"
                      min={0}
                      step={1}
                      value={payouts[place]}
                      onChange={e => updatePayout(place, e.target.value)}
                      placeholder="0"
                      style={{ maxWidth: 140 }}
                    />
                  </div>
                </td>
                <td style={{ color: Number(payouts[place]) > 0 ? 'var(--green)' : 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.88rem' }}>
                  {Number(payouts[place]) > 0 ? `Will show: $${Number(payouts[place]).toLocaleString()}` : 'Hidden'}
                </td>
              </tr>
            ))}

            {/* Separator */}
            <tr><td colSpan={3} style={{ padding: '4px 0' }}><div style={{ borderTop: '2px solid var(--slate)' }} /></td></tr>

            {/* Last Place */}
            <tr>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.2rem' }}>💀</span>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700 }}>Last Place</span>
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)' }}>$</span>
                  <input
                    className="form-input"
                    type="number"
                    min={0}
                    step={1}
                    value={payouts.last}
                    onChange={e => updatePayout('last', e.target.value)}
                    placeholder="0"
                    style={{ maxWidth: 140 }}
                  />
                </div>
              </td>
              <td style={{ color: Number(payouts.last) > 0 ? '#e67e22' : 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.88rem' }}>
                {Number(payouts.last) > 0 ? `Will show: $${Number(payouts.last).toLocaleString()}` : 'Hidden'}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Summary */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 20, padding: '12px 16px', background: 'rgba(75,139,190,0.06)', borderRadius: 8, border: '1px solid rgba(75,139,190,0.15)' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>Active Places</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue)' }}>{activePlaces}</div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>Total Payouts</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--green)' }}>${totalPot.toLocaleString()}</div>
          </div>
          {paymentSummary && paymentSummary.entryFee > 0 && (<>
            <div style={{ width: 1, height: 40, background: 'var(--slate)', margin: '0 4px' }} />
            <div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>Collected</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--green)' }}>
                ${(paymentSummary.paidCount * paymentSummary.entryFee).toLocaleString()}
              </div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)' }}>
                {paymentSummary.paidCount} of {paymentSummary.total} paid
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>Outstanding</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: paymentSummary.unpaidCount > 0 ? '#e67e22' : 'var(--gray)' }}>
                ${(paymentSummary.unpaidCount * paymentSummary.entryFee).toLocaleString()}
              </div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)' }}>
                {paymentSummary.unpaidCount} unpaid
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>Net vs Payouts</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: (paymentSummary.paidCount * paymentSummary.entryFee) >= totalPot ? 'var(--green)' : 'var(--red)' }}>
                ${((paymentSummary.paidCount * paymentSummary.entryFee) - totalPot).toLocaleString()}
              </div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)' }}>
                collected minus payouts
              </div>
            </div>
          </>)}
        </div>

        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Payout Settings'}
        </button>
      </div>
    </div>
  );
}
